import { Divider, VStack, Box, Card, CardBody, CardHeader, Text } from '@cad-arc';
import { UnitHistoryItemsGroup, UnitHistoryItemView } from '@mark43/cad-api';
import { useCallback, useMemo } from 'react';
import { intersperse } from '../../../common/helpers/intersperse';
import { formatDate } from '../../../common/helpers/utils';
import { EventsTitle } from './events-title';
import { useOpenDepartmentLink } from '../../../layout/use-open-department-link';
import UnitHistoryItemRow from './unit-history-item-row';

type UnitHistoryItemsDateGroup = {
    dateHeader: string;
    unitHistoryItemViews: UnitHistoryItemView[];
};

function groupByDate(unitHistoryItemViews: UnitHistoryItemView[]): UnitHistoryItemsDateGroup[] {
    const groups: UnitHistoryItemsDateGroup[] = [];
    let currentGroup: UnitHistoryItemsDateGroup | undefined = undefined;
    for (const item of unitHistoryItemViews) {
        const currentGroupDateHeader: string | undefined = currentGroup
            ? currentGroup.dateHeader
            : undefined;
        const currentItemDateHeader = formatDate(item.createdDateUtc);
        if (currentGroup === undefined || currentItemDateHeader !== currentGroupDateHeader) {
            currentGroup = {
                dateHeader: currentItemDateHeader,
                unitHistoryItemViews: [],
            };
            groups.push(currentGroup);
        }
        currentGroup.unitHistoryItemViews.push(item);
    }
    return groups;
}

export function UnitHistoryItemsCard({
    unitHistoryItemsGroup,
    index,
}: {
    unitHistoryItemsGroup: UnitHistoryItemsGroup;
    index: number;
}): React.ReactElement {
    const unitHistoryItemsDateGroups = useMemo(() => {
        return groupByDate(unitHistoryItemsGroup.unitHistoryItemViews);
    }, [unitHistoryItemsGroup.unitHistoryItemViews]);

    const openDepartmentLink = useOpenDepartmentLink();
    const openRenRmsLink = useCallback(
        (renRmsLink: string) => {
            openDepartmentLink({
                tenantStrategy: 'PREFER_RMS_TENANT',
                path: `search/cad?ren=${renRmsLink}`,
                app: 'RMS',
            });
        },
        [openDepartmentLink]
    );

    return (
        <Card style={{ width: '100%', scrollMarginTop: 8 }} data-index={index}>
            {unitHistoryItemsGroup.events.length > 0 ? (
                <CardHeader>
                    <EventsTitle events={unitHistoryItemsGroup.events} />
                </CardHeader>
            ) : undefined}
            <CardBody>
                <VStack spacing="16px" align="left">
                    {intersperse(
                        unitHistoryItemsDateGroups.map((unitHistoryItemsDateGroup, idx) => (
                            <VStack spacing="16px" align="left" key={idx}>
                                <Text variant="headingXs" color="tertiary">
                                    {unitHistoryItemsDateGroup.dateHeader}
                                </Text>
                                {unitHistoryItemsDateGroup.unitHistoryItemViews.map(
                                    (unitHistoryItemView) => (
                                        <UnitHistoryItemRow
                                            key={unitHistoryItemView.id}
                                            unitHistoryItemView={unitHistoryItemView}
                                            openRenRmsLink={openRenRmsLink}
                                        />
                                    )
                                )}
                            </VStack>
                        )),
                        <Divider />
                    ).map((element, idx) => (
                        <Box key={idx}>{element}</Box>
                    ))}
                </VStack>
            </CardBody>
        </Card>
    );
}
